import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
   Image,
   Button,
   Modal,
   InputNumber,
   Row,
   Col,
   Input,
   Select,
   Form
} from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../../../../contexts/language";
import { getImagePath } from "../../../../utils/getImagePath";
import { useAuth } from "../../../../contexts/auth/index";

const { Option } = Select;

interface IProps {
   visible: boolean;
   onOk: (requestedItemsInfo: any) => any;
   confirmLoading: boolean;
   onCancel: () => any;
   items: any[];
   material: any;
   demandData: any;
}

export const RequestModal = ({
   visible,
   onOk,
   confirmLoading,
   onCancel,
   items,
   material,
   demandData
}: IProps) => {
   const [t] = useTranslation();
   const [selectedItems, setSelectedItems] = useState(items);
   const [showEditForm, setShowEditForm] = useState([false, false, false]);
   const [deliveryAddressForm] = Form.useForm();
   const [invoiceAddressForm] = Form.useForm();
   const [contactDetailsForm] = Form.useForm();
   const [formsValidate, setFormsValidate] = useState([true, true, true]);
   const locale = useLocale();
   const { user: currentUser } = useAuth();

   const validateMessages = {
      required:
         locale.locale == "en"
            ? "${label} is missing!"
            : "${label} wird vermisst!"
   };

   useEffect(() => {
      setSelectedItems([
         ...selectedItems.sort(
            (a, b) => a.data.operations_id - b.data.operations_id
         )
      ]);
      selectedItems.forEach((item) => {
         item["logistics"] = "shipping";
         item["deconstruction"] = item.data.deconstructability.includes("loose")
            ? null
            : "provided";
         if (
            material.hasOwnProperty("building_id") &&
            item.data.deconstructability.includes("loose")
         ) {
            const sameOperationIds = selectedItems.filter(
               (_item) => _item.data.operations_id == item.data.operations_id
            );
            const notLoose = sameOperationIds.find(
               (_item) => !_item.data.deconstructability.includes("loose")
            );
            notLoose
               ? (item["deconstruction"] = "provided")
               : (item["deconstruction"] = null);
         }
      });
   }, []);

   const totalPrice = () => {
      return selectedItems.reduce(
         (a: any, b: { [x: string]: any }, i: number) =>
            a + (getPrice(i, b) || 0),
         0
      );
   };

   const [deliveryAddress, setDeliveryAddress] = useState({
      street: demandData?.location?.places_api_data?.Street,
      zip: demandData?.location?.places_api_data?.PostalCode,
      city: demandData?.location?.places_api_data?.Municipality
   });

   const [invoiceAddress, setInvoiceAddress] = useState({
      company: "",
      street: "",
      zip: "",
      city: ""
   });

   const [contactDetails, setContactDetails] = useState({
      firstName: currentUser?.attributes?.given_name,
      lastName: currentUser?.attributes?.family_name,
      phone: "",
      email: currentUser?.attributes?.email
   });

   const editContactInformation = (index: number) => {
      const newStatus = [false, false, false];
      newStatus[index] = !showEditForm[index];
      setShowEditForm(newStatus);
   };

   const deconstructionChanged = (value: string, index: number) => {
      if (value == "self") {
         selectedItems[index].logistics = "pickup";
      }
      selectedItems[index].deconstruction = value;
      selectedItems.forEach((item, i) => {
         if (
            item.data.operations_id == selectedItems[index].data.operations_id
         ) {
            selectedItems[i].deconstruction = value;
            selectedItems[i].logistics = selectedItems[index].logistics;
         }
      });
      setSelectedItems([...selectedItems]);
   };

   const logisticsChanged = (value: string, index: number) => {
      selectedItems[index].logistics = value;
      material.hasOwnProperty("building_id") &&
         selectedItems.forEach((item, i) => {
            if (
               item.data.operations_id ==
               selectedItems[index].data.operations_id
            ) {
               selectedItems[i].logistics = value;
            }
         });
      setSelectedItems([...selectedItems]);
   };

   const updateDeliveryAddress = (value: string | number, key: string) => {
      setDeliveryAddress((state) => ({ ...state, [key]: value }));
   };

   const updateInvoiceAddress = (value: string | number, key: string) => {
      setInvoiceAddress((state) => ({ ...state, [key]: value }));
   };

   const updateContactDetails = (value: string | number, key: string) => {
      setContactDetails((state) => ({ ...state, [key]: value }));
   };

   const deleteItem = (index: number) => {
      const _selectedItems = selectedItems.filter((_, i) => i != index);
      _selectedItems.forEach((item) => {
         if (item.data.deconstructability.includes("loose")) {
            const sameOperationIds = _selectedItems.filter(
               (_item) => _item.data.operations_id == item.data.operations_id
            );
            const notLoose = sameOperationIds.find(
               (_item) => !_item.data.deconstructability.includes("loose")
            );
            if (!notLoose) item["deconstruction"] = null;
         }
      });
      setSelectedItems([..._selectedItems]);
   };

   const changeSelectedPieces = (index: number, value: number) => {
      selectedItems[index].amount = value;
      setSelectedItems([...selectedItems]);
   };

   const sendRequest = async () => {
      deliveryAddressForm.submit();
      invoiceAddressForm.submit();
      contactDetailsForm.submit();
      await deliveryAddressForm
         .validateFields()
         .then(async () => {
            formsValidate[0] = true;
            setFormsValidate([...formsValidate]);
         })
         .catch((error) => {
            formsValidate[0] = false;
            setFormsValidate([...formsValidate]);
         });
      await invoiceAddressForm
         .validateFields()
         .then(async () => {
            formsValidate[1] = true;
            setFormsValidate([...formsValidate]);
         })
         .catch((error) => {
            formsValidate[1] = false;
            setFormsValidate([...formsValidate]);
         });
      await contactDetailsForm
         .validateFields()
         .then(async () => {
            formsValidate[2] = true;
            setFormsValidate([...formsValidate]);
         })
         .catch((error) => {
            formsValidate[2] = false;
            setFormsValidate([...formsValidate]);
         });

      const requestedItemsInfo = {
         totalPrice: totalPrice(),
         deliveryAddress: deliveryAddress,
         invoiceAddress: invoiceAddress,
         contactDetails: contactDetails,
         items: selectedItems
      };
      formsValidate.every((element) => element === true) &&
         onOk(requestedItemsInfo);
   };

   const getPrice = (index: number, item: any) => {
      /*       let deconstructionCost = 0;
      if (
         item?.data?.deconstructability &&
         item?.data?.deconstructability === "loose (easy)"
      ) {
         deconstructionCost = 1;
      } else if (
         item?.data?.deconstructability &&
         [
            "built-in (easy)",
            "built-in (moderate)",
            "built-in (elaborate)"
         ].indexOf(item?.data?.deconstructability) !== -1 &&
         item?.data?.third_party_deconstruction
      ) {
         deconstructionCost = 1;
      } else {
         deconstructionCost = 0;
      } */
      /*       return (
         (item?.data?.selling_price -
            ((material?.demands_target_price
               ? material?.demands_target_price
               : 0) -
               material?.deconstruction_costs * deconstructionCost)) *
         selectedItems[index].amount
      ); */
      let deconstructionCost = 0;
      if (selectedItems[index]["deconstruction"] === "provided") {
         deconstructionCost = 1;
      }
      //console.log('selectedItems', selectedItems);
      return (
         item?.data?.selling_price * selectedItems[index].amount +
         material?.deconstruction_costs *
            deconstructionCost *
            selectedItems[index].amount
      );
   };

   const totalPieces = () => {
      return selectedItems
         .reduce((a: any, b: { [x: string]: any }) => a + (b["amount"] || 0), 0)
         .toLocaleString();
   };

   function allFieldsFilledOut(currentObject: {}) {
      return Object.values(currentObject).every((element) => element != "");
   }

   return (
      <Modal
         visible={visible}
         okText={t("default.sendRequest")}
         onOk={sendRequest}
         confirmLoading={confirmLoading}
         onCancel={onCancel}
         width={1200}
         style={{ borderRadius: "20px" }}
         bodyStyle={{ borderRadius: "20px" }}
         centered
      >
         <div className="requestform">
            <div className="header">
               <h3 className="title">{t("passportDetails.request")}</h3>
            </div>
            <Row gutter={24} justify="space-between" className="material-info">
               <Col span={2} className="material-image">
                  <Image
                     src={
                        material?.images
                           ? getImagePath(material?.images[0])
                           : "error"
                     }
                     fallback="data:image/png;base64,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"
                  />
               </Col>
               <Col span={22} className="products">
                  <h3 className="title">
                     {material?.manufacturer} {material.product_name}
                  </h3>
                  <div className="material-list">
                     {selectedItems &&
                        selectedItems.map((item: any, index: number) => {
                           return (
                              <div className="cluster-row">
                                 <Row
                                    gutter={24}
                                    className="requested-passport"
                                 >
                                    <Col span={7}>
                                       <div className="bold-title">{`${t(
                                          "passportDetails.condition"
                                       )}: ${item.data.condition}`}</div>
                                    </Col>
                                    <Col span={5}>
                                       <div className="bold-title">
                                          {`${item.data.deconstructability}`}
                                       </div>
                                       <div className="subtitle">
                                          {item.data.deconstructability.includes(
                                             "loose"
                                          )
                                             ? t(
                                                  "passportDetails.noDeconstructionNeeded"
                                               )
                                             : ""}
                                       </div>
                                    </Col>
                                    <Col
                                       span={12}
                                       className="display-flex pieces"
                                    >
                                       <div className="available-pieces">
                                          <span>{item.data.amount}</span>
                                          {` ${
                                             item?.data?.amount_unit == "Stück"
                                                ? t("passportDetails.pieces")
                                                : item?.data?.amount_unit
                                          } ${t("passportDetails.available")}`}
                                       </div>
                                       <div className="selected-pieces">
                                          <div className="input">
                                             <InputNumber
                                                value={item.amount}
                                                onChange={(e) =>
                                                   changeSelectedPieces(
                                                      index,
                                                      e
                                                   )
                                                }
                                                max={item.data.amount}
                                                min={1}
                                             />
                                          </div>
                                          <span>
                                             {item.data.amount_unit == "Stück"
                                                ? t("passportDetails.pieces")
                                                : item?.amount_unit}
                                          </span>
                                       </div>
                                       <div className="item-price">
                                          <span className="price">{`${getPrice(
                                             index,
                                             item
                                          ).toLocaleString()} €`}</span>{" "}
                                          <Button
                                             className={`delete-btn ${
                                                selectedItems.length == 1
                                                   ? "disabled"
                                                   : ""
                                             }`}
                                             type="link"
                                             icon={<DeleteOutlined />}
                                             onClick={() => deleteItem(index)}
                                             disabled={
                                                selectedItems.length == 1
                                             }
                                          ></Button>
                                       </div>
                                    </Col>
                                 </Row>
                                 {(item?.data?.operations_id !=
                                    selectedItems[index + 1]?.data
                                       ?.operations_id ||
                                    !material.hasOwnProperty(
                                       "building_id"
                                    )) && (
                                    <Row className="deconst-info">
                                       <Col
                                          span={9}
                                          className={
                                             item.deconstruction &&
                                             !material?.building_id
                                                ? "disabled"
                                                : ""
                                          }
                                       >
                                          <div>
                                             <span className="bold-title">
                                                {t("operations.deconstruction")}
                                             </span>
                                             <Select
                                                disabled={
                                                   !material?.building_id
                                                }
                                                defaultValue={
                                                   item.deconstruction
                                                }
                                                value={item.deconstruction}
                                                style={{ width: 120 }}
                                                onChange={(e) =>
                                                   deconstructionChanged(
                                                      e,
                                                      index
                                                   )
                                                }
                                             >
                                                <Option value="provided">
                                                   {t(
                                                      "passportDetails.provided"
                                                   )}
                                                </Option>
                                                {item.data
                                                   .start_date_deconstruction &&
                                                   item.data
                                                      .third_party_deconstruction && (
                                                      <Option value="self">
                                                         {t(
                                                            "passportDetails.self"
                                                         )}
                                                      </Option>
                                                   )}
                                             </Select>
                                          </div>
                                          {item.deconstruction ? (
                                             item.deconstruction ==
                                             "provided" ? (
                                                material.hasOwnProperty(
                                                   "building_id"
                                                ) && (
                                                   <div className="sub-description">
                                                      <div>
                                                         {t(
                                                            "passportDetails.deconstructionPlanned"
                                                         )}
                                                      </div>
                                                      <div>{`${t(
                                                         "default.from"
                                                      )} ${moment(
                                                         item.data
                                                            .start_date_deconstruction
                                                      ).format(
                                                         "DD/MM/YYYY"
                                                      )} ${t(
                                                         "default.to"
                                                      )} ${moment(
                                                         item.data
                                                            .end_date_deconstruction
                                                      ).format(
                                                         "DD/MM/YYYY"
                                                      )}`}</div>
                                                   </div>
                                                )
                                             ) : (
                                                <div className="sub-description self-deconst">
                                                   <div className="attention">
                                                      {t(
                                                         "passportDetails.pleaseNote"
                                                      )}
                                                   </div>
                                                   <div>
                                                      {t(
                                                         "passportDetails.selfDeconstructionPossible"
                                                      )}
                                                   </div>
                                                   <div>{`${t(
                                                      "default.between"
                                                   )} ${moment(
                                                      item.data
                                                         .self_start_date_deconstruction
                                                   ).format(
                                                      "DD/MM/YYYY"
                                                   )} - ${moment(
                                                      item.data
                                                         .self_end_date_deconstruction
                                                   ).format(
                                                      "DD/MM/YYYY"
                                                   )}`}</div>
                                                   <div className="subtitle mt-5">
                                                      {t(
                                                         "passportDetails.customerSupportCall"
                                                      )}
                                                   </div>
                                                </div>
                                             )
                                          ) : null}
                                       </Col>
                                       <Col span={7}>
                                          <div>
                                             <span className="bold-title">
                                                {t("operations.logistics")}
                                             </span>
                                             <Select
                                                defaultValue={item.logistics}
                                                value={item.logistics}
                                                style={{ width: 120 }}
                                                onChange={(e) =>
                                                   logisticsChanged(e, index)
                                                }
                                                disabled={
                                                   item.deconstruction == "self"
                                                }
                                             >
                                                <Option value="shipping">
                                                   {t("operations.shipping")}
                                                </Option>
                                                <Option value="pickup">
                                                   {t("operations.pickup")}
                                                </Option>
                                             </Select>
                                          </div>
                                          {item.logistics == "shipping" ? (
                                             <div className="sub-description">
                                                {t(
                                                   "passportDetails.deliveryToAddress"
                                                )}
                                             </div>
                                          ) : (
                                             <div className="sub-description">
                                                <div>
                                                   {t(
                                                      "passportDetails.pickupAddress"
                                                   )}
                                                </div>
                                                <div className="pickup-address">
                                                   {material?.building_id
                                                      ? material.building_location_of
                                                      : material.material_current_location}
                                                </div>
                                                <div className="subtitle">
                                                   {t(
                                                      "passportDetails.exactAddressAfter"
                                                   )}
                                                </div>
                                             </div>
                                          )}
                                       </Col>
                                    </Row>
                                 )}
                              </div>
                           );
                        })}
                  </div>
                  <Row>
                     <Col span={6} offset={18} className="total-price">
                        <div className="display-flex">
                           <div>{`${totalPieces()} ${
                              selectedItems[0].data.amount_unit == "Stück"
                                 ? t("passportDetails.pieces")
                                 : selectedItems[0].data.amount_unit
                           }`}</div>
                           <div>{`${totalPrice().toLocaleString()} €`}</div>
                        </div>
                        <div className="shipping-cost">{`+ ${t(
                           "passportDetails.shippingCosts"
                        )}`}</div>
                        <div className="calculation-info">
                           {t("passportDetails.calculateAfterRequest")}
                        </div>
                     </Col>
                  </Row>
               </Col>
            </Row>
            <Row className="details">
               <Col span={24}>
                  <Form
                     form={deliveryAddressForm}
                     validateMessages={validateMessages}
                  >
                     <div className="display-flex row">
                        <div className="display-flex">
                           <div className="title">{`${t(
                              "passportDetails.destination"
                           )} / ${t("passportDetails.deliveryAddress")}`}</div>
                           <div className="address">
                              {`${deliveryAddress.street}, ${deliveryAddress.zip} ${deliveryAddress.city}`}
                           </div>
                        </div>
                        <div className="display-flex">
                           {!formsValidate[0] && (
                              <div className="validation-warning">
                                 <ExclamationCircleOutlined />
                                 <span>
                                    {t("passportDetails.filloutMissing")}
                                 </span>
                              </div>
                           )}
                           <Button
                              type="primary"
                              className="edit"
                              shape="round"
                              onClick={() => editContactInformation(0)}
                           >
                              {showEditForm[0]
                                 ? t("default.close")
                                 : t("default.edit")}
                           </Button>
                        </div>
                     </div>
                     <div
                        className={`display-flex info-inputs ${
                           !showEditForm[0] && "disabled"
                        }`}
                     >
                        <div className="input">
                           <div className="label">{`${t("default.street")}, ${t(
                              "default.number"
                           )}`}</div>
                           <Form.Item
                              name={[t("default.street")]}
                              rules={[{ required: true }]}
                              initialValue={deliveryAddress.street}
                           >
                              <Input
                                 value={deliveryAddress.street}
                                 onChange={(e) =>
                                    updateDeliveryAddress(
                                       e.target.value,
                                       "street"
                                    )
                                 }
                              />
                           </Form.Item>
                        </div>
                        <div className="input">
                           <div className="label">{t("default.zip")}</div>
                           <Form.Item
                              name={[t("default.zip")]}
                              rules={[{ required: true }]}
                              initialValue={deliveryAddress.zip}
                              className="no-arrow"
                           >
                              <Input
                                 type="number"
                                 value={deliveryAddress.zip}
                                 onChange={(e) =>
                                    updateDeliveryAddress(e.target.value, "zip")
                                 }
                              />
                           </Form.Item>
                        </div>
                        <div className="input">
                           <div className="label">{t("default.city")}</div>
                           <Form.Item
                              name={[t("default.city")]}
                              rules={[{ required: true }]}
                              initialValue={deliveryAddress.city}
                           >
                              <Input
                                 value={deliveryAddress.city}
                                 onChange={(e) =>
                                    updateDeliveryAddress(
                                       e.target.value,
                                       "city"
                                    )
                                 }
                              />
                           </Form.Item>
                        </div>
                     </div>
                  </Form>
               </Col>
               <Col span={24}>
                  <Form
                     form={invoiceAddressForm}
                     validateMessages={validateMessages}
                  >
                     <div className="display-flex row">
                        <div className="display-flex">
                           <div className="title">{`${t(
                              "passportDetails.invoiceAddress"
                           )}`}</div>
                           {invoiceAddress.company && (
                              <div className="address">
                                 {`${invoiceAddress.company}, ${invoiceAddress.street}, ${invoiceAddress.zip} ${invoiceAddress.city}`}
                              </div>
                           )}
                        </div>
                        <div className="display-flex">
                           {!formsValidate[1] && (
                              <div className="validation-warning">
                                 <ExclamationCircleOutlined />
                                 <span>
                                    {t("passportDetails.filloutMissing")}
                                 </span>
                              </div>
                           )}
                           <Button
                              type="primary"
                              className={
                                 showEditForm[1]
                                    ? "close"
                                    : allFieldsFilledOut(invoiceAddress)
                                    ? "edit"
                                    : "add"
                              }
                              shape="round"
                              onClick={() => editContactInformation(1)}
                           >
                              {showEditForm[1]
                                 ? t("default.close")
                                 : allFieldsFilledOut(invoiceAddress)
                                 ? t("default.edit")
                                 : t("default.add")}
                           </Button>
                        </div>
                     </div>
                     <div
                        className={`info-inputs ${
                           !showEditForm[1] && "disabled"
                        }`}
                     >
                        <div className="input">
                           <div className="label">{t("default.company")}</div>
                           <Form.Item
                              name={[t("default.company")]}
                              rules={[{ required: true }]}
                           >
                              <Input
                                 value={invoiceAddress.company}
                                 onChange={(e) =>
                                    updateInvoiceAddress(
                                       e.target.value,
                                       "company"
                                    )
                                 }
                              />
                           </Form.Item>
                        </div>
                        <div className="display-flex">
                           <div className="input">
                              <div className="label">{`${t(
                                 "default.street"
                              )}, ${t("default.number")}`}</div>
                              <Form.Item
                                 name={[t("default.street")]}
                                 rules={[{ required: true }]}
                              >
                                 <Input
                                    value={invoiceAddress.street}
                                    onChange={(e) =>
                                       updateInvoiceAddress(
                                          e.target.value,
                                          "street"
                                       )
                                    }
                                 />
                              </Form.Item>
                           </div>
                           <div className="input">
                              <div className="label">{t("default.zip")}</div>
                              <Form.Item
                                 name={[t("default.zip")]}
                                 rules={[{ required: true }]}
                                 className="no-arrow"
                              >
                                 <Input
                                    type="number"
                                    value={invoiceAddress.zip}
                                    onChange={(e) =>
                                       updateInvoiceAddress(
                                          e.target.value,
                                          "zip"
                                       )
                                    }
                                 />
                              </Form.Item>
                           </div>
                           <div className="input">
                              <div className="label">{t("default.city")}</div>
                              <Form.Item
                                 name={[t("default.city")]}
                                 rules={[{ required: true }]}
                              >
                                 <Input
                                    value={invoiceAddress.city}
                                    onChange={(e) =>
                                       updateInvoiceAddress(
                                          e.target.value,
                                          "city"
                                       )
                                    }
                                 />
                              </Form.Item>
                           </div>
                        </div>
                     </div>
                  </Form>
               </Col>
               <Col span={24}>
                  <Form
                     form={contactDetailsForm}
                     validateMessages={validateMessages}
                  >
                     <div className="display-flex row">
                        <div className="display-flex">
                           <div className="title">{`${t(
                              "passportDetails.contactDetails"
                           )}`}</div>
                           {contactDetails.email && (
                              <div className="address">
                                 {`${contactDetails.firstName} ${contactDetails.lastName}, ${contactDetails.email}, ${contactDetails.phone}`}
                              </div>
                           )}
                        </div>
                        <div className="display-flex">
                           {!formsValidate[2] && (
                              <div className="validation-warning">
                                 <ExclamationCircleOutlined />
                                 <span>
                                    {t("passportDetails.filloutMissing")}
                                 </span>
                              </div>
                           )}
                           <Button
                              type="primary"
                              className={
                                 showEditForm[2]
                                    ? "close"
                                    : allFieldsFilledOut(contactDetails)
                                    ? "edit"
                                    : "add"
                              }
                              shape="round"
                              onClick={() => editContactInformation(2)}
                           >
                              {showEditForm[2]
                                 ? t("default.close")
                                 : allFieldsFilledOut(contactDetails)
                                 ? t("default.edit")
                                 : t("default.add")}
                           </Button>
                        </div>
                     </div>
                     <div
                        className={`info-inputs ${
                           !showEditForm[2] && "disabled"
                        }`}
                     >
                        <div className="display-flex">
                           <div className="input">
                              <div className="label">
                                 {t("default.firstName")}
                              </div>
                              <div>
                                 <Form.Item
                                    name={[t("default.firstName")]}
                                    rules={[{ required: true }]}
                                    initialValue={contactDetails.firstName}
                                 >
                                    <Input
                                       value={contactDetails.firstName}
                                       onChange={(e) =>
                                          updateContactDetails(
                                             e.target.value,
                                             "firstName"
                                          )
                                       }
                                    />
                                 </Form.Item>
                              </div>
                           </div>
                           <div className="input">
                              <div className="label">
                                 {t("default.lastName")}
                              </div>
                              <div>
                                 <Form.Item
                                    name={[t("default.lastName")]}
                                    rules={[{ required: true }]}
                                    initialValue={contactDetails.lastName}
                                 >
                                    <Input
                                       value={contactDetails.lastName}
                                       onChange={(e) =>
                                          updateContactDetails(
                                             e.target.value,
                                             "lastName"
                                          )
                                       }
                                    />
                                 </Form.Item>
                              </div>
                           </div>
                        </div>
                        <div className="display-flex">
                           <div className="input">
                              <div className="label">{t("default.phone")}</div>
                              <Form.Item
                                 name={[t("default.phone")]}
                                 rules={[{ required: true }]}
                                 className="no-arrow"
                                 initialValue={contactDetails.phone}
                              >
                                 <Input
                                    type={"number"}
                                    width={150}
                                    value={contactDetails.phone}
                                    onChange={(e) =>
                                       updateContactDetails(
                                          e.target.value,
                                          "phone"
                                       )
                                    }
                                 />
                              </Form.Item>
                           </div>
                           <div className="input">
                              <div className="label">E-Mail</div>
                              <Form.Item
                                 name={["Email"]}
                                 rules={[{ type: "email", required: true }]}
                                 initialValue={contactDetails.email}
                              >
                                 <Input
                                    onChange={(e) =>
                                       updateContactDetails(
                                          e.target.value,
                                          "email"
                                       )
                                    }
                                 />
                              </Form.Item>
                           </div>
                        </div>
                     </div>
                  </Form>
               </Col>
            </Row>
         </div>
      </Modal>
   );
};
